import React, { useState, useEffect } from 'react';
import { Search, Trash2, Edit, Package, IndianRupee, Eye, X, RefreshCw, AlertCircle } from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const API_BASE_URL = process.env.REACT_APP_BACKEND_API || 'http://127.0.0.1:8000';

const ComboList = () => {
  const [combos, setCombos] = useState([]);
  const [filteredCombos, setFilteredCombos] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, combo: null });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    total_pages: 1
  });

  const fetchCombos = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/combo/list/?page=${page}&limit=${pagination.limit}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch combos: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Transform API data to match component structure
      const transformedCombos = data.results.map(combo => ({
        id: combo.id,
        _id: combo._id,
        title: combo.title,
        description: combo.description,
        price: combo.price,
        images: combo.images || [],
        image1: combo.images?.[0] || null,
        image2: combo.images?.[1] || null,
        createdAt: combo.created_at,
        updatedAt: combo.updated_at,
        status: combo.status,
        is_featured: combo.is_featured
      }));

      setCombos(transformedCombos);
      setFilteredCombos(transformedCombos);
      setPagination({
        total: data.total,
        page: data.page,
        limit: data.limit,
        total_pages: data.total_pages
      });
      
    } catch (err) {
      console.error('Error fetching combos:', err);
      setError(err.message || 'Failed to fetch combos');
      toast.error('Failed to fetch combos', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCombos();
  }, []);

  useEffect(() => {
    const filtered = combos.filter(combo =>
      combo.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      combo.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      combo.id.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredCombos(filtered);
  }, [searchTerm, combos]);

  const handleDelete = async (comboId) => {
    try {
      setDeleteLoading(true);
      
      const response = await fetch(`${API_BASE_URL}/combo/delete/${comboId}/`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete combo');
      }

      // Get the response data to show success message
      const result = await response.json();

      // Remove from local state
      const updatedCombos = combos.filter(combo => combo.id !== comboId);
      setCombos(updatedCombos);
      setFilteredCombos(updatedCombos);
      setDeleteModal({ isOpen: false, combo: null });
      
      // Update pagination total
      setPagination(prev => ({
        ...prev,
        total: prev.total - 1
      }));

      // Show success toast
      toast.success(`Combo "${deleteModal.combo?.title}" deleted successfully!`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

    } catch (err) {
      console.error('Error deleting combo:', err);
      setError(err.message || 'Failed to delete combo');
      
      // Show error toast
      toast.error(err.message || 'Failed to delete combo', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const openDeleteModal = (combo) => {
    setDeleteModal({ isOpen: true, combo });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, combo: null });
  };

  const handleRefresh = () => {
    fetchCombos(pagination.page);
    toast.info('Refreshing combo list...', {
      position: "top-right",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Unknown';
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(price);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Toast Container with better positioning */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      
      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center justify-between">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 mr-2" />
            <span>{error}</span>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-red-700 hover:text-red-900"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Search and Stats */}
      <div className="flex justify-between items-center">
        <div className="relative w-64">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            placeholder="Search combos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="flex items-center space-x-4">
          <span className="text-sm text-gray-600">
            {searchTerm ? `${filteredCombos.length} filtered from ${pagination.total}` : `${pagination.total} total combos`}
          </span>
          <button
            onClick={handleRefresh}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            title="Refresh"
          >
            <RefreshCw className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Combos Grid */}
      {filteredCombos.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredCombos.map((combo) => (
            <div key={combo._id} className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              {/* Image Gallery */}
              <div className="relative">
                {combo.images.length >= 2 ? (
                  <div className="grid grid-cols-2 gap-1">
                    <img
                      src={combo.image1}
                      alt={`${combo.title} - Image 1`}
                      className="w-full h-32 object-cover rounded-tl-lg"
                      onError={(e) => {
                        e.target.src = "https://via.placeholder.com/300x200?text=No+Image";
                      }}
                    />
                    <img
                      src={combo.image2}
                      alt={`${combo.title} - Image 2`}
                      className="w-full h-32 object-cover rounded-tr-lg"
                      onError={(e) => {
                        e.target.src = "https://via.placeholder.com/300x200?text=No+Image";
                      }}
                    />
                  </div>
                ) : (
                  <img
                    src={combo.image1 || "https://via.placeholder.com/300x200?text=No+Image"}
                    alt={combo.title}
                    className="w-full h-32 object-cover rounded-t-lg"
                    onError={(e) => {
                      e.target.src = "https://via.placeholder.com/300x200?text=No+Image";
                    }}
                  />
                )}
                
                <div className="absolute top-2 right-2">
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                    combo.status === 'active' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {combo.status}
                  </span>
                </div>

                {combo.is_featured && (
                  <div className="absolute top-2 left-2">
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                      Featured
                    </span>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900 line-clamp-1">
                    {combo.title}
                  </h3>
                  <div className="flex items-center text-indigo-600 font-bold">
                    <IndianRupee className="h-4 w-4" />
                    <span>{combo.price}</span>
                  </div>
                </div>

                <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                  {combo.description}
                </p>

                <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                  <span>Created: {formatDate(combo.createdAt)}</span>
                  <div className="flex items-center">
                    <Package className="h-3 w-3 mr-1" />
                    <span>{combo.id}</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-2">
                  <button className="flex-1 bg-indigo-600 text-white text-sm py-2 px-3 rounded hover:bg-indigo-700 transition-colors flex items-center justify-center">
                    <Eye className="h-4 w-4 mr-1" />
                    View
                  </button>
                  <button className="bg-gray-100 text-gray-700 text-sm py-2 px-3 rounded hover:bg-gray-200 transition-colors flex items-center justify-center">
                    <Edit className="h-4 w-4" />
                  </button>
                  <button 
                    onClick={() => openDeleteModal(combo)}
                    className="bg-red-100 text-red-700 text-sm py-2 px-3 rounded hover:bg-red-200 transition-colors flex items-center justify-center"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Package className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No combos found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm ? 'Try adjusting your search terms.' : 'Get started by creating a new combo.'}
          </p>
          {!searchTerm && (
            <button
              onClick={handleRefresh}
              className="mt-3 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-600 bg-indigo-100 hover:bg-indigo-200"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </button>
          )}
        </div>
      )}

      {/* Pagination Info */}
      {pagination.total > 0 && (
        <div className="text-center text-sm text-gray-500">
          Showing page {pagination.page} of {pagination.total_pages} ({pagination.total} total combos)
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Confirm Delete</h3>
              <button
                onClick={closeDeleteModal}
                disabled={deleteLoading}
                className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-10 h-10 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
              </div>
              
              <div className="text-center">
                <h4 className="text-lg font-medium text-gray-900 mb-2">
                  Delete "{deleteModal.combo?.title}"?
                </h4>
                <p className="text-sm text-gray-500 mb-2">
                  ID: {deleteModal.combo?.id}
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  This action cannot be undone. This combo will be permanently removed from your inventory.
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={closeDeleteModal}
                  disabled={deleteLoading}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteModal.combo.id)}
                  disabled={deleteLoading}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                      Deleting...
                    </div>
                  ) : (
                    'Delete'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComboList;